import { useState, useCallback, useEffect } from 'react'
import { useAppSelector } from '../../../../../state/hooks'
import { selectSaleLocalId } from '../../../../../state/slices/currentSale/currentSaleSlice'
import {
  fetchRepeatPaymentMethodCheck,
  RepeatPaymentMethodResult,
} from '../../../../../functions/repeat-payment-method'
import { retailerSettingsManager } from '../../../../../managers/retailer-settings-manager'
import { registerManager } from '../../../../../ng-compat/managers'

interface RepeatPaymentMethodFetchResult {
  data: RepeatPaymentMethodResult | undefined
  isLoading: boolean
  refetch: () => Promise<void>
}

export const useRepeatPaymentMethodRequests = (
  enabled = false
): RepeatPaymentMethodFetchResult => {
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const retailerId = retailerSettingsManager.get().id
  const activeOutlet = registerManager.getActiveOutlet()
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<RepeatPaymentMethodResult | undefined>()

  const fetchRepeatPaymentMethodsResponse = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await fetchRepeatPaymentMethodCheck(
        saleLocalId,
        retailerId,
        activeOutlet.id
      )
      setData(result)
      setIsLoading(false)
    } catch (error) {
      const fallbackData = {
        orderId: saleLocalId,
        repeatCardOccurrenceCount: 0,
        brand: null,
        last4: null,
        isSaved: false,
      }
      setData(fallbackData)
      setIsLoading(false)
    }
  }, [saleLocalId, retailerId, activeOutlet.id])

  useEffect(() => {
    if (!enabled || !saleLocalId || !retailerId || !activeOutlet.id) {
      return
    }

    fetchRepeatPaymentMethodsResponse()
  }, [
    enabled,
    saleLocalId,
    retailerId,
    activeOutlet.id,
    fetchRepeatPaymentMethodsResponse,
  ])

  return {
    data,
    isLoading,
    refetch: fetchRepeatPaymentMethodsResponse,
  }
}
